'use client'

import Image from 'next/image'
import GradientText from './GradientText'
import { trackEvent } from '../utils/analytics'

export default function Footer() {
  return (
    <footer className="text-white relative bg-cover bg-top bg-no-repeat z-20 -mt-32" style={{ boxShadow: '0 -20px 40px rgba(0, 0, 0, 0.4)' }}>
      {/* Background Image with Lazy Loading */}
      <Image
        src="/images/footer_background_monet.webp"
        alt=""
        fill
        loading="lazy"
        className="object-cover object-top z-[-10]"
        sizes="100vw"
      />
      
      {/* Top shadow overlay for depth effect */}
      <div className="absolute top-0 left-0 right-0 h-12 bg-gradient-to-b from-black/40 to-transparent pointer-events-none z-30"></div>
      
      {/* Progressive dark overlay - gets stronger below the button */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/60 from-20% via-black/85 via-40% to-black/95"></div>
      
      {/* Final CTA Section */}
      <section className="pt-16 sm:pt-24 pb-8 sm:pb-12 lg:pb-16 relative z-10">
        <div className="container-stripe">
          <div className="text-center mt-4 sm:mt-8 px-4 sm:px-0">
            <h2 className="text-3xl sm:text-4xl md:text-6xl font-light mb-4 text-balance text-center max-w-5xl mx-auto text-white leading-tight drop-shadow-lg tracking-tight">
              Stop Guessing.
              <span className="block mt-2 sm:mt-3 mb-2 font-normal" style={{ paddingBottom: '8px' }}>
                <GradientText colors={["#ffffff", "#bfdbfe", "#60a5fa", "#bfdbfe", "#ffffff"]}>
                  Start Outmaneuvering.
                </GradientText>
              </span>
            </h2>
            <p className="text-sm sm:text-base md:text-lg font-light mb-8 sm:mb-12 max-w-3xl mx-auto text-center text-blue-100/95 leading-relaxed drop-shadow-sm">
              Your next big move deserves more than a leap of faith. Give it a winning strategy. Simulate your GTM today and launch with the confidence of a market leader.
            </p>
            <div className="flex justify-center items-center">
              <a
                href="https://app.venturecompass.ai/signup"
                className="glassmorphism-button group inline-flex items-center px-6 py-3 sm:px-8 sm:py-4 text-white font-normal text-base sm:text-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50 focus-visible:ring-offset-2"
                onClick={() => trackEvent('cta_click', { location: 'footer', text: 'Find Your Winning Strategy' })}
              >
                <span className="hidden sm:inline">Find Your Winning Strategy</span>
                <span className="sm:hidden">Get Your Strategy</span>
                <svg className="ml-2 w-4 h-4 sm:w-5 sm:h-5 transition-transform duration-300 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer Content */}
      <div className="relative z-10">
        <div className="container-stripe pt-20 sm:pt-32 pb-12 sm:pb-20 px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-12">
            {/* Company Info */}
            <div>
              <div className="mb-6 sm:mb-8">
                <div className="mb-4 sm:mb-6">
                  <GradientText className="!mx-0 !justify-start" colors={["#ffffff", "#bfdbfe", "#60a5fa", "#bfdbfe", "#ffffff"]}>
                    <span className="text-xl sm:text-2xl font-normal">VentureCompass</span>
                  </GradientText>
                </div>
                <p className="text-sm sm:text-base md:text-lg font-light max-w-md text-blue-100/95 leading-relaxed drop-shadow-sm">
                  Empowering founders with AI-driven insights and strategies to build, scale, and succeed in today's competitive landscape.
                </p>
              </div>
              
              {/* Social Links */}
              <div className="flex space-x-4 sm:space-x-6">
                <a
                  href="https://x.com/venturecompass"
                  className="text-text-muted hover:text-primary-500 transition-colors duration-200 focus-ring rounded p-1"
                  aria-label="Follow us on X (formerly Twitter)"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackEvent('social_click', { platform: 'x_twitter' })}
                >
                  <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                </a>
                <a
                  href="https://www.linkedin.com/company/venture-compass-ai/"
                  className="text-text-muted hover:text-primary-500 transition-colors duration-200 focus-ring rounded p-1"
                  aria-label="Follow us on LinkedIn"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackEvent('social_click', { platform: 'linkedin' })}
                >
                  <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </a>
              </div>
            </div>

            {/* Navigation Links */}
            <div className="text-left md:text-right">
              <h3 className="text-lg sm:text-xl font-normal text-white mb-4 sm:mb-6">Product</h3>
              <nav aria-label="Footer navigation">
                <div className="flex flex-col gap-3 sm:gap-4 items-start md:items-end">
                  <a
                    href="#features"
                    className="text-sm sm:text-base font-light text-blue-100/95 leading-relaxed drop-shadow-sm hover:text-white transition-colors duration-200 focus-ring rounded"
                    onClick={() => trackEvent('nav_click', { destination: 'features', location: 'footer' })}
                  >
                    Features
                  </a>
                  <a
                    href="#who-its-for"
                    className="text-sm sm:text-base font-light text-blue-100/95 leading-relaxed drop-shadow-sm hover:text-white transition-colors duration-200 focus-ring rounded"
                    onClick={() => trackEvent('nav_click', { destination: 'solutions', location: 'footer' })}
                  >
                    Solutions
                  </a>
                  <a
                    href="#how-it-works"
                    className="text-sm sm:text-base font-light text-blue-100/95 leading-relaxed drop-shadow-sm hover:text-white transition-colors duration-200 focus-ring rounded"
                    onClick={() => trackEvent('nav_click', { destination: 'how_it_works', location: 'footer' })}
                  >
                    How It Works
                  </a>
                </div>
              </nav>
            </div>
          </div>

          {/* Bottom Section */}
          <div className="mt-12 sm:mt-16 pt-6 sm:pt-8 border-t border-text-muted/20">
            <div className="flex flex-col sm:flex-row justify-between items-center">
              <div className="flex flex-wrap gap-4 sm:gap-6 text-sm text-text-muted mb-4 sm:mb-0 justify-center sm:justify-start">
                <a
                  href="/privacy"
                  className="text-xs sm:text-sm font-light text-blue-100/95 leading-relaxed drop-shadow-sm hover:text-primary-500 transition-colors duration-200 focus-ring rounded"
                  onClick={() => trackEvent('legal_link_click', { page: 'privacy' })}
                >
                  Privacy Policy
                </a>
                <a
                  href="/terms"
                  className="text-xs sm:text-sm font-light text-blue-100/95 leading-relaxed drop-shadow-sm hover:text-primary-500 transition-colors duration-200 focus-ring rounded"
                  onClick={() => trackEvent('legal_link_click', { page: 'terms' })}
                >
                  Terms of Service
                </a>
                <a
                  href="/cookies"
                  className="text-xs sm:text-sm font-light text-blue-100/95 leading-relaxed drop-shadow-sm hover:text-primary-500 transition-colors duration-200 focus-ring rounded"
                  onClick={() => trackEvent('legal_link_click', { page: 'cookies' })}
                >
                  Cookie Policy
                </a>
                <a
                  href="/security"
                  className="text-xs sm:text-sm font-light text-blue-100/95 leading-relaxed drop-shadow-sm hover:text-primary-500 transition-colors duration-200 focus-ring rounded"
                  onClick={() => trackEvent('legal_link_click', { page: 'security' })}
                >
                  Security
                </a>
              </div>
            <div className="text-xs sm:text-sm font-light text-blue-100/95 leading-relaxed drop-shadow-sm text-center sm:text-left">
              © 2025 VentureCompass™. All Rights Reserved.
            </div>
            </div>
          </div>
        </div>

      </div>
    </footer>
  )
}